import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    IconButton,
    Typography,
    Button,
    Fade,
    Tooltip,
    useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import HandymanIcon from '@mui/icons-material/Handyman';
import WorkIcon from '@mui/icons-material/Work';
import CommentIcon from '@mui/icons-material/Comment';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import {
    HeaderContainer,
    LogoContainer,
    NavLinksContainer,
    HamburgerButton,
} from './Header.styles';

const pages = [
    { name: 'Home', path: '#home', icon: <HomeIcon /> },
    { name: 'Services', path: '/#services', icon: <HandymanIcon /> },
    { name: 'Projects', path: '/#projects', icon: <WorkIcon /> },
    { name: 'Testimonials', path: '/#testimonials', icon: <CommentIcon /> },
    { name: 'Contact', path: '/#contact', icon: <ContactMailIcon /> },
];

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();

    const handleNavClick = (path) => {
        setMobileMenuOpen(false);

        if (path.includes('#')) {
            const sectionId = path.split('#')[1];

            // For home, scroll to top of page
            if (sectionId === 'home') {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
                return;
            }

            const element = document.getElementById(sectionId);
            if (element) {
                // Calculate header height to offset scroll position
                const headerHeight =
                    document.querySelector('.MuiAppBar-root')?.offsetHeight ||
                    70;
                // Add additional offset for better spacing
                const additionalOffset = 30;
                const elementPosition =
                    element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition =
                    elementPosition - headerHeight - additionalOffset;

                // Scroll with offset
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            } else {
                navigate(path);
            }
        } else {
            navigate(path);
        }
    };

    const handleHamburgerClick = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    return (
        <HeaderContainer>
            {/* Hamburger button with inline mobile navigation buttons */}
            <Box
                sx={{
                    display: { xs: 'flex', sm: 'none' },
                    order: 1,
                    padding: '0 10px',
                    position: 'relative',
                    alignItems: 'center',
                }}
            >
                <HamburgerButton
                    component={IconButton}
                    aria-label="menu"
                    onClick={handleHamburgerClick}
                    color="primary"
                    sx={{
                        backgroundColor: mobileMenuOpen
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(255, 255, 255, 0.2)',
                        '& .MuiSvgIcon-root': {
                            color: mobileMenuOpen ? 'white' : undefined,
                        },
                    }}
                >
                    {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                </HamburgerButton>

                {/* Minimalist mobile buttons - visible only when hamburger is clicked */}
                <Fade in={mobileMenuOpen}>
                    <Box
                        sx={{
                            display: mobileMenuOpen ? 'flex' : 'none',
                            alignItems: 'center',
                            ml: 1,
                            gap: 1,
                        }}
                    >
                        {pages.map((page) => (
                            <IconButton
                                key={page.name}
                                size="small"
                                onClick={() => handleNavClick(page.path)}
                                sx={{
                                    color: 'text.primary',
                                    '&:hover': {
                                        color: 'primary.light',
                                    },
                                }}
                            >
                                {page.icon}
                            </IconButton>
                        ))}
                    </Box>
                </Fade>
            </Box>

            {/* Logo on the right for mobile */}
            <LogoContainer
                sx={{
                    display: { xs: 'flex' },
                    justifyContent: {
                        xs: 'flex-end',
                        sm: 'flex-start',
                    },
                    flexGrow: { xs: 1, sm: 0 }, // Only flex grow on mobile
                    order: { xs: 2, sm: 1 },
                }}
                onClick={() => handleNavClick('#home')}
            >
                <Box
                    sx={{
                        position: 'relative',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backdropFilter: 'blur(10px)',
                        mr: { md: 0, lg: 2 }, // Only add margin in lg and up
                    }}
                >
                    <img
                        src={'/assets/logo.webp'}
                        loading="eager"
                        alt="Done Right Home Services Logo"
                        height="50"
                        style={{
                            display: 'block',
                            position: 'relative',
                            zIndex: 0,
                            opacity: 0.8, // Fixed: Use a simple number value
                        }}
                    />
                </Box>
                <Typography
                    variant="h6"
                    noWrap
                    sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                        textDecoration: 'none',
                        display: { xs: 'none', lg: 'block' },
                        fontSize: { lg: '1.1rem', xl: '1.25rem' }, // Smaller font on lg screens
                        whiteSpace: { lg: 'nowrap' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    Done Right Home Services
                </Typography>
            </LogoContainer>

            {/* Desktop/Tablet menu with icons and text - hidden on xs */}
            <NavLinksContainer
                sx={{
                    display: { xs: 'none', sm: 'flex' },
                    order: 2,
                    flexGrow: 1, // Always take available space
                    justifyContent: {
                        sm: 'flex-end',
                        md: 'center',
                        lg: 'center',
                    }, // Force center alignment
                    px: { lg: 4 }, // Add padding on large screens
                    '& > div': {
                        // Add responsive spacing between navigation items
                        mx: { sm: 0.5, md: 1, lg: 2, xl: 3 },
                    },
                }}
            >
                {pages.map((page) => (
                    <Box key={page.name}>
                        {/* On sm screens - show icons with tooltip */}
                        <Tooltip title={page.name}>
                            <Button
                                onClick={() => handleNavClick(page.path)}
                                sx={{
                                    my: 1,
                                    color: 'text.primary',
                                    minWidth: 'auto',
                                    px: { sm: 1, md: 1.5 },
                                    display: {
                                        sm: 'flex',
                                        md: 'none',
                                    },

                                    '&:hover': {
                                        color: 'primary.light',
                                        backgroundColor:
                                            'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                {page.icon}
                            </Button>
                        </Tooltip>

                        {/* On md and larger screens - show icons with text */}
                        <Button
                            sx={{
                                my: 1,
                                color: 'text.primary',
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                px: { md: 1 },
                                '&:hover': {
                                    color: 'primary.light',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                                fontSize: {
                                    xl: '1rem',
                                }, // Smaller font on md screens
                            }}
                            onClick={() => handleNavClick(page.path)}
                        >
                            <Box
                                sx={{
                                    mr: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {page.icon}
                            </Box>
                            {page.name}
                        </Button>
                    </Box>
                ))}
            </NavLinksContainer>

            {/* Contact button */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    order: 3,
                    ml: { md: 1, lg: 2 },
                    mr: 1,
                    alignItems: 'center',
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleNavClick('/#contact')}
                    sx={{
                        whiteSpace: 'nowrap',
                        px: { md: 1, lg: 2 },
                        [theme.breakpoints.up('lg')]: {
                            // Use media query for larger size
                            padding: theme.spacing(0.75, 2.5),
                            fontSize: '0.875rem',
                        },
                    }}
                >
                    Get a Quote
                </Button>
            </Box>
        </HeaderContainer>
    );
};

export default Header;
